import { Campaign, SortField, SortOrder } from '../types/Campaign';

const statusOrder = { active: 1, pending: 2, done: 3 };

export const sortCampaigns = (
  campaigns: Campaign[],
  field: SortField,
  order: SortOrder
): Campaign[] => {
  const sorted = [...campaigns].sort((a, b) => {
    // First, always sort by status
    const statusDiff = statusOrder[a.status] - statusOrder[b.status];
    if (statusDiff !== 0) return statusDiff;

    // Then sort by the selected field
    let aValue: string | number | null = a[field];
    let bValue: string | number | null = b[field];

    // Handle null values conditionally
    if (aValue === null && bValue === null) return 0;

    // Special cases: nulls first
    if (field === 'startDate' && order === 'asc') {
      if (aValue === null) return -1;
      if (bValue === null) return 1;
    }
    if (field === 'endDate' && order === 'desc') {
      if (aValue === null) return -1;
      if (bValue === null) return 1;
    }

    // Default: nulls last
    if (aValue === null) return 1;
    if (bValue === null) return -1;

    // Convert dates to timestamps for comparison
    if (field === 'startDate' || field === 'endDate') {
      aValue = new Date(aValue as string).getTime();
      bValue = new Date(bValue as string).getTime();
    }

    // Handle string comparison (case-insensitive)
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (aValue < bValue) return order === 'asc' ? -1 : 1;
    if (aValue > bValue) return order === 'asc' ? 1 : -1;
    return 0;
  });

  return sorted;
};

export const filterCampaignsByBrand = (
  campaigns: Campaign[],
  brandFilter: string
): Campaign[] => {
  if (!brandFilter.trim() || brandFilter === 'all') return campaigns;

  return campaigns.filter((campaign) => campaign.brandName === brandFilter);
};

export const filterCampaignsBySearch = (
  campaigns: Campaign[],
  searchQuery: string
): Campaign[] => {
  if (!searchQuery.trim()) return campaigns;

  const query = searchQuery.toLowerCase();
  return campaigns.filter((campaign) =>
    campaign.campaignTitle.toLowerCase().includes(query)
  );
};

export const filterCampaignsBySquad = (
  campaigns: Campaign[],
  squadFilter: string
): Campaign[] => {
  if (!squadFilter.trim() || squadFilter === 'all') return campaigns;

  return campaigns.filter((campaign) => campaign.squad === squadFilter);
};

export const getUniqueBrands = (campaigns: Campaign[]): string[] => {
  const brands = campaigns.map((campaign) => campaign.brandName);
  return Array.from(new Set(brands)).sort();
};
